import styled from '@emotion/styled';
import { Box, Flex, Text } from '@qga/roo-ui/components';

import { CtaEvent } from '@shared/v2/components/createRoomOptionCTA/types';
import { PackageOption } from '@shared/v2/utils/getPackageOptions';

import AlertBanner from '@/v2/components/ui/AlertBanner';
import { useOtherPackageModal } from '../../../../contexts/OtherPackageModalContext';
import { useRoomOptionsData } from '../../../../contexts/RoomOptionsContext';
import { OnCtaClickOpts } from '../../../RoomOptions/hooks/useCtaOnClick/useCtaOnClick';

interface InfoCopy {
  [key: string]: {
    heading: string;
    body: string;
  };
}

const TEXT: InfoCopy = {
  'info-unavailable-for-po': {
    heading: 'This room is available for your dates in another package option',
    body: 'Select another package option below to continue booking this room.',
  },
  'info-unavailable-on-dates': {
    heading: 'This room is not part of your currently selected package option',
    body: 'Select another package option below to continue entering dates.',
  },
};

type InfoProps = {
  offers: any[];
  isWarning?: boolean;
  packageOption: PackageOption;
  isPreSearch?: boolean;
};

type GetCopy = InfoProps & {
  onCtaClick?: (event: CtaEvent, opts: OnCtaClickOpts) => void;
  closeOtherPackageModal: () => void;
};

const ChangeDatesLink = styled(Text)`
  cursor: pointer;
`;

const getCopy = ({
  packageOption,
  offers,
  isWarning,
  onCtaClick,
  closeOtherPackageModal,
  isPreSearch,
}: GetCopy) => {
  if (isWarning) {
    return {
      heading: `${packageOption.title} is unavailable for your selected dates`,
      body: (
        <Text
          fontSize="xs"
          lineHeight="20px"
          letterSpacing="0.1px"
          color="text.heading"
        >
          Select another package option below or{' '}
          <ChangeDatesLink
            fontSize="xs"
            lineHeight="20px"
            letterSpacing="0.1px"
            onClick={() => {
              onCtaClick?.('CHANGE_DATES', {});
              closeOtherPackageModal();
            }}
          >
            change your dates
          </ChangeDatesLink>
        </Text>
      ),
    };
  }

  let variant = 'info-unavailable-on-dates';
  if (!isPreSearch && offers?.length > 0) {
    variant = 'info-unavailable-for-po';
  }

  return {
    heading: TEXT[variant].heading,
    body: TEXT[variant].body,
  };
};

const Info = ({ offers, isWarning, packageOption, isPreSearch }: InfoProps) => {
  const { onCtaClick } = useRoomOptionsData() || {};
  const { closeOtherPackageModal } = useOtherPackageModal();

  const { heading, body } = getCopy({
    packageOption,
    isWarning,
    offers,
    closeOtherPackageModal,
    onCtaClick,
    isPreSearch,
  });

  return (
    <Box>
      <AlertBanner
        variant={isWarning ? 'warningFill' : 'info'}
        iconColor={isWarning ? 'orange' : undefined}
      >
        <Flex flexDirection="column">
          <Text fontSize="xs" color="initial" fontWeight="bold">
            {heading}
          </Text>
          <Box>
            <Text fontSize="xs" color="greys.steel">
              {body}
            </Text>
          </Box>
        </Flex>
      </AlertBanner>
    </Box>
  );
};

export default Info;
